import { type AnimationProps, motion, useReducedMotion } from "framer-motion";
import React, { Component } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { connect, type ConnectedProps } from "react-redux";
import { change } from "redux-form";

import {
  CourseId,
  getCourseTag,
  isAdditionalAlignersCourse,
  isOneArchIncludedInCourse,
  isRetainersCourse,
  versionize,
} from "~/common/courses";
import { remoteLog } from "~/common/logging";
import { Arch } from "~/common/prescription";
import { canOrderTestPlastic } from "~/common/user";
import { MaterialOptions } from "~/components/patient/patient_shared/material_options";
import { TestPlaticOptions } from "~/components/patient/patient_shared/test_plastic_options";
import { Label } from "~/components/ui/label";
import { LabelledCheckbox } from "~/components/ui/labelled-checkbox";
import { LabelledRadio, LabelledRadioGroup } from "~/components/ui/labelled-radio";
import { TextArea } from "~/components/ui/textarea";
import { QuestionTooltip } from "~/components/ui/tooltip";
import {
  getCourseVersion,
  isCBCTIncludedInCourse,
  isCBCTServiceAvailableForCourse,
  type TCourseInstallment,
} from "~/slices/course-installment";
import { addInstructions, removeInstructions } from "~/slices/instructions";
import { selectCorrectionFormValues } from "~/slices/redux-form";
import type { RootState } from "~/store";
import type { TPatient } from "~/types/patient";

const mapStateToProps = (state: RootState) => ({
  user: state.user,
  instructions: state.instructions,
  formValues: selectCorrectionFormValues(state),
});

const mapDispatchToProps = {
  addInstructions,
  removeInstructions,
  change,
};

type PatientNewCourseProps = PropsFromRedux & {
  setDirty(key: string, data: unknown): void;
  courseInstallment: TCourseInstallment;
};

class PatientNewCourse extends Component<PatientNewCourseProps> {
  constructor(props: PatientNewCourseProps) {
    super(props);
    this.selectCourse = this.selectCourse.bind(this);
    this.addCondition = this.addCondition.bind(this);
    this.selectDeepCBCTAnalysis = this.selectDeepCBCTAnalysis.bind(this);
  }

  componentDidCatch(e: Error) {
    remoteLog(e, "patient_new_course");
  }

  selectCourse(course_id: CourseId) {
    const { instructions, courseInstallment, formValues } = this.props;

    this.props.addInstructions({ course_id });
    this.props.setDirty("course_id", course_id);

    const coursePaymentOptions = courseInstallment[course_id];

    if (
      instructions.course_id != course_id &&
      instructions.payment_option_id &&
      !(instructions.payment_option_id in coursePaymentOptions)
    ) {
      this.props.addInstructions({ payment_option_id: null });
    }

    if (formValues?.arch == Arch.BOTH && isOneArchIncludedInCourse(course_id)) {
      this.props.change("correction", "arch", null);
    }

    this.props.addInstructions({ deep_cbct: null });

    if (isRetainersCourse(course_id)) {
      this.props.removeInstructions({ sber_credit: false });
    }
  }

  selectDeepCBCTAnalysis(data: boolean) {
    this.props.addInstructions({ deep_cbct: data });
  }

  addCondition(data: string) {
    this.props.addInstructions({ condition: data });
    this.props.setDirty("condition", data);
  }

  render() {
    const { user, courseInstallment } = this.props;
    const { course_id = null, deep_cbct, test_plastic } = this.props.instructions;

    return (
      <div id="course-section" style={{ marginTop: "20px" }}>
        <PatientNewCourseOptions
          selectedCourse={course_id}
          courseInstallment={courseInstallment}
          onCourseChange={(course) => this.selectCourse(course)}
        />

        {/*
         * If you use AnimatePresence here, then if you switch from course that displays this warning to
         * retainers, then material options have a nasty buggy layout shift on the right of the screen.
         * So i decided to remove AnimatePresence alltogether, feels better for that edge case.
         */}
        {(course_id != null && isCBCTIncludedInCourse(courseInstallment, course_id)) ||
        deep_cbct ? (
          <ScanUploadCTWarning />
        ) : null}

        {isRetainersCourse(course_id) || isAdditionalAlignersCourse(course_id) ? null : (
          <DiagnosisTextArea onChange={(e) => this.addCondition(e.target.value)} />
        )}

        {isAdditionalAlignersCourse(course_id) ? null : <MaterialOptions />}

        {canOrderTestPlastic(user) ? (
          <TestPlaticOptions
            value={test_plastic}
            onValueChange={(test_plastic) => this.props.addInstructions({ test_plastic })}
          />
        ) : null}

        {course_id == null || isCBCTServiceAvailableForCourse(courseInstallment, course_id) ? (
          <div id="deep-cbct-section">
            <h5 id="deep-cbct-label" className="block" style={{ fontWeight: 900 }}>
              <FormattedMessage id="ADDITIONAL_INSTRUCTIONS" />
            </h5>

            <LabelledCheckbox
              label={<FormattedMessage id="C_DEEP_CBCT_ANALYSIS" />}
              name="deep_cbct"
              checked={deep_cbct == true}
              onChange={(e) => this.selectDeepCBCTAnalysis(e.currentTarget.checked)}
            />
          </div>
        ) : null}
      </div>
    );
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(PatientNewCourse);

function PatientNewCourseOptions(props: {
  selectedCourse: number | null;
  courseInstallment: TCourseInstallment;
  onCourseChange(course: CourseId): void;
}) {
  return (
    <CourseRadioGroup>
      <CourseRadio {...props} isNew course={CourseId.LIMITLESS} />
      <CourseRadio {...props} course={CourseId.FULL} />
      <CourseRadio {...props} course={CourseId.TEEN} />
      <CourseRadio {...props} course={CourseId.SHORT} />
      <CourseRadio {...props} course={CourseId.SINGLE_ARCH} />
      <CourseRadio {...props} course={CourseId.SUPER_SHORT} />
      <CourseRadio {...props} course={CourseId.PLAN_3D} />
      <CourseRadio {...props} course={CourseId.CHILDREN} tooltip={<ChildrenV3Tooltip />} />
      <CourseRadio {...props} course={CourseId.CHILDREN_SHORT} tooltip={<ChildrenShortTooltip />} />
      <CourseRadio {...props} course={CourseId.RETAINERS} />
      <CourseRadio {...props} course={CourseId.ADDITIONAL_ALIGNERS} />
    </CourseRadioGroup>
  );
}

export function CourseRadioGroup({ children }: React.PropsWithChildren<object>) {
  return (
    <LabelledRadioGroup
      className="form-group"
      label={<FormattedMessage id="HEADER_INSTRUCTIONS" />}
      labelProps={{
        id: "validation-course_id",
        size: "xl",
        tooltip: (
          <>
            <FormattedMessage id="course.type.tooltip" />
            <a href="/api/v1/manuals/document/2" rel="noopener noreferrer" target="_blank">
              <FormattedMessage id="tooltip.chart" />
            </a>
            <br />
            <FormattedMessage id="course.type.tooltip.sec" />
          </>
        ),
      }}
      required
    >
      {children}
    </LabelledRadioGroup>
  );
}

export function CourseRadio({
  course,
  selectedCourse,
  courseInstallment,
  onCourseChange,
  patient,
  isNew = false,
  tooltip,
  image,
}: {
  course: CourseId;
  selectedCourse: number | null;
  courseInstallment: TCourseInstallment;
  onCourseChange(newCourse: CourseId): void;
  patient?: TPatient;
  isNew?: boolean;
  tooltip?: React.ReactChild | null;
  image?: React.ReactChild;
}) {
  const courseVersion = getCourseVersion(courseInstallment, course);
  const isCourseActive = courseVersion != null;

  if (!isCourseActive) {
    return null;
  }

  const title = versionize(getCourseTag(course), courseVersion);
  const description = versionize(`${getCourseTag(course)}_COMMENT`, courseVersion);

  return (
    <LabelledRadio
      label={
        <div className="course_title">
          {image}
          {image ? <br /> : null}

          <span className={course == selectedCourse ? "boldText" : undefined}>
            {isNew ? (
              <img
                src="/img/teenNew.svg"
                alt=""
                width={40}
                height={22}
                style={{ marginRight: 5 }}
              />
            ) : null}

            {/* @ts-expect-error some courses don't have all the versions of the other courses */}
            <FormattedMessage id={title} defaultMessage=" " />
          </span>

          {/* @ts-expect-error some courses don't have all the versions of the other courses */}
          <FormattedMessage id={description} defaultMessage=" " />
          {tooltip}
        </div>
      }
      align="start"
      name="course_id"
      defaultValue={course}
      defaultChecked={patient?.course.course_id == course}
      onChange={() => onCourseChange(course)}
    />
  );
}

export function DiagnosisTextArea({
  diagnosis,
  onChange,
}: {
  diagnosis?: string | null;
  onChange: React.ChangeEventHandler<HTMLTextAreaElement>;
}) {
  const intl = useIntl();

  return (
    <div className="form-group">
      <Label id="validation-condition" htmlFor="patient-diagnosis-value">
        <FormattedMessage id="HEADER_CONDITION" />
      </Label>

      <TextArea
        id="patient-diagnosis-value"
        data-matomo-mask
        name="diagnos"
        placeholder={intl.formatMessage({ id: "CONDITION_PLACEHOLDER" })}
        defaultValue={diagnosis ?? undefined}
        onChange={onChange}
      />
    </div>
  );
}

export function ScanUploadCTWarning() {
  const shouldReduceMotion = useReducedMotion();

  const animationProps: AnimationProps = {
    initial: { height: 0 },
    animate: { height: "auto", transition: { duration: 0.5 } },
    exit: { height: 0 },
  };

  return (
    <div className="col-xs-12 tw-overflow-hidden">
      <motion.p className="tw-m-0" {...(shouldReduceMotion ? {} : animationProps)}>
        <span className="alert-warning tw-block tw-p-4">
          <FormattedMessage id="SCAN_UPLOAD_CT_WARNING" />
        </span>
        <span className="tw-block tw-h-5" />
      </motion.p>
    </div>
  );
}

export function ChildrenV3Tooltip() {
  return (
    <QuestionTooltip>
      Доступен к заказу при соблюдении у пациента следующих условий:
      <ol className="tw-mt-2 tw-list-none tw-space-y-0.5 tw-pl-2 tw-italic">
        <li>А. Должны быть прорезавшимися постоянные первые моляры</li>
        <li>Б. Не менее 2 постоянных резцов должны быть прорезавшимися не менее, чем на 2/3</li>
        <li>В. Не менее 2 зубов (III или IV или V) в одном квадранте должны быть в наличии</li>
        <li>Г. Отсутствие постоянных 7 зубов</li>
      </ol>
      <p className="tw-m-0">
        Курс включает в себя неограниченное количество элайнеров и коррекций{" "}
        <strong>в течение 18 месяцев</strong>.
      </p>
      <p className="tw-m-0 tw-mt-2">
        <strong>Внимание!</strong> Дубликаты не входят в стоимость курса и при необходимости
        заказываются отдельно в соответствии с действующим прайсом.
      </p>
    </QuestionTooltip>
  );
}

export function ChildrenShortTooltip() {
  return (
    <QuestionTooltip>
      Первый комплект доступен для заказа при соблюдении у пациента следующих условий:
      <ol className="tw-mt-2 tw-list-none tw-space-y-0.5 tw-pl-2 tw-italic">
        <li>А. Должны быть прорезавшимися постоянные первые моляры</li>
        <li>Б. Не менее 2 постоянных резцов должны быть прорезавшимися не менее, чем на 2/3</li>
        <li>В. Не менее 2 зубов (III или IV или V) в одном квадранте должны быть в наличии</li>
        <li>Г. Отсутствие постоянных 7 зубов</li>
      </ol>
      <p className="tw-m-0">
        Комплект включает в себя до <strong>24 КАП</strong>, которые возможно использовать для
        лечения как на одной, так и на двух челюстях.
      </p>
      <p className="tw-m-0 tw-mt-2">
        <strong>Внимание!</strong> В рамках одной заявки можно заказать не более{" "}
        <strong>ТРЕХ</strong> комплектов (стоимость каждого комплекта составляет 40 000 рублей).
      </p>
      <p className="tw-m-0 tw-mt-2">
        Второй и третий комплекты можно заказать вне зависимости от того, прорезались 7 зубы или
        нет, в течение трех лет с момента заказа первого комплекта и до достижения пациентом 13 лет.
      </p>
    </QuestionTooltip>
  );
}
